import { type TellescopeModelFromName } from "../../mappings";
import { useTellescopeInfiniteQuery } from "../use-tellescope-infinite-query";

/**
 * Fetches users from the Tellescope API with optional filtering by user IDs.
 *
 * This hook uses infinite query pagination to efficiently load user records.
 * When userIds are provided, it filters the results to only include matching users.
 * When userIds is undefined, it loads all users.
 *
 * @param userIds - Optional array of user IDs to filter by. If provided and empty,
 *                  the query is disabled. If undefined, all users are loaded.
 * @param enabled - Optional boolean to enable/disable query. Defaults to enabled.
 *
 * @returns An infinite query result containing:
 *   - data: Array of user records (filtered by userIds if provided)
 *   - isLoading: Loading state indicator
 *   - error: Error object if the query failed
 *   - fetchNextPage: Function to load more pages
 *   - hasNextPage: Whether more pages are available
 *
 * @example
 * ```tsx
 * // Load all users
 * const { data: allUsers, isLoading } = useUsersQuery();
 *
 * // Load specific users by ID
 * const { data: specificUsers } = useUsersQuery(['user1', 'user2']);
 *
 * // Query disabled when empty array provided
 * const { data } = useUsersQuery([]); // Query will not execute
 * ```
 *
 * @remarks
 * - Uses cursor-based pagination with a limit of 1000 records per page
 * - Automatically disabled when an empty userIds array is provided
 * - Client-side filtering is applied when userIds are specified
 */
export const useUsersQuery = (userIds?: string[], enabled: boolean = true) => {
  return useTellescopeInfiniteQuery({
    model: "users",
    method: "getSome",
    request: { limit: 1000 },
    select: (data) =>
      data.pages.reduce<TellescopeModelFromName<"users">[]>((acc, page) => {
        if (userIds?.length) {
          acc.push(...page.filter((user) => userIds.includes(user.id)));
        } else {
          acc.push(...page);
        }
        return acc;
      }, []),
    enabled: enabled && (userIds === undefined || userIds.length > 0),
  });
};
